import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { 
  Loader2, CreditCard, ArrowLeft, Check, AlertCircle, 
  Lock, Shield, Mail, Eye, EyeOff 
} from 'lucide-react';
import { api } from '../lib/api';
import { setTokens } from '../lib/utils';

// Stripe promise
let stripePromise: Promise<any> | null = null;

const getStripe = async () => {
  if (!stripePromise) {
    const { data } = await api.get('/billing/stripe-config');
    stripePromise = loadStripe(data.publishable_key);
  }
  return stripePromise;
};

interface PlanInfo {
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  currency: string;
  features: string[];
  trial_days: number;
}

// Payment Form Component (shown after account creation)
function PaymentForm({ 
  clientSecret, 
  plan,
  accessToken,
  onSuccess,
  onError 
}: { 
  clientSecret: string;
  plan: { name: string; price: number; currency: string; interval: string; trial_days: number };
  accessToken: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Erreur de validation');
        setProcessing(false);
        return;
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/billing/success`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Erreur de paiement');
        onError(confirmError.message || 'Erreur de paiement');
      } else {
        // Payment successful - save token and redirect
        setTokens(accessToken, undefined);
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Erreur inattendue');
      onError(err.message || 'Erreur inattendue');
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(price);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-white font-medium">{plan.name}</p>
            <p className="text-sm text-zinc-400">
              Facturation {plan.interval === 'year' ? 'annuelle' : 'mensuelle'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-white">
              {formatPrice(plan.price, plan.currency)}
            </p>
            <p className="text-xs text-zinc-500">/{plan.interval === 'year' ? 'an' : 'mois'}</p>
          </div>
        </div>
        {plan.trial_days > 0 && (
          <div className="mt-3 pt-3 border-t border-zinc-700">
            <p className="text-sm text-emerald-400 flex items-center gap-2">
              <Check className="w-4 h-4" />
              {plan.trial_days} jours d'essai gratuit
            </p>
          </div>
        )}
      </div>

      {/* Payment Element */}
      <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-300 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-4 px-6 bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Traitement...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            {plan.trial_days > 0 ? 'Démarrer l\'essai gratuit' : 'Payer et accéder'}
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-4 text-xs text-zinc-500">
        <span className="flex items-center gap-1"><Shield className="w-3 h-3" />Paiement sécurisé</span>
        <span>•</span>
        <span>Powered by Stripe</span>
      </div>
    </form>
  );
}

// Main Unified Checkout Page
export default function UnifiedCheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const planSlug = searchParams.get('plan');
  const interval = searchParams.get('interval') || 'month';
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Process state
  const [step, setStep] = useState<'form' | 'payment'>('form');
  const [loading, setLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Stripe state
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [planDetails, setPlanDetails] = useState<any>(null);
  
  // Plan info for display
  const [plan, setPlan] = useState<PlanInfo | null>(null);

  // Load plan details
  useEffect(() => {
    if (!planSlug) {
      setError('Aucun plan spécifié');
      setPlanLoading(false);
      return;
    }

    const fetchPlan = async () => {
      try {
        const { data } = await api.get(`/billing/plans/${planSlug}`);
        setPlan(data);
        
        // Load Stripe
        const stripe = await getStripe();
        setStripePromise(Promise.resolve(stripe));
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Plan non trouvé');
      } finally {
        setPlanLoading(false);
      }
    };

    fetchPlan();
  }, [planSlug]);

  // Handle form submission (create account + prepare payment)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/billing/unified-checkout', null, {
        params: { plan_slug: planSlug, interval, email, password }
      });

      setClientSecret(data.client_secret);
      setAccessToken(data.access_token);
      setPlanDetails(data.plan);
      setStep('payment');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth login
  const handleOAuth = (provider: 'google' | 'github') => {
    // Store plan info for after OAuth
    sessionStorage.setItem('checkout_plan', planSlug || '');
    sessionStorage.setItem('checkout_interval', interval);
    
    // Redirect to OAuth
    window.location.href = `/api/fapi/auth/${provider}/authorize?scopes=email`;
  };

  const handlePaymentSuccess = () => {
    setSuccess(true);
    setTimeout(() => {
      // Fetch user info and redirect
      navigate('/billing/success');
    }, 1500);
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(price);
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Paiement réussi !</h1>
          <p className="text-zinc-400 mb-4">Redirection en cours...</p>
          <Loader2 className="w-5 h-5 text-emerald-400 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Loading state
  if (planLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  // Error state (no plan)
  if (error && !plan) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Erreur</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link 
            to="/#pricing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux offres
          </Link>
        </div>
      </div>
    );
  }

  const currentPrice = interval === 'year' && plan?.price_yearly 
    ? plan.price_yearly 
    : plan?.price_monthly || 0;

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Back link */}
        <Link 
          to="/#pricing"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux offres
        </Link>

        {/* Main Card */}
        <div className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-5 text-center">
            <CreditCard className="w-8 h-8 text-white/80 mx-auto mb-2" />
            <h1 className="text-lg font-bold text-white">
              S'abonner à {plan?.name}
            </h1>
            <p className="text-emerald-100 text-2xl font-bold mt-1">
              {formatPrice(currentPrice, plan?.currency || 'EUR')}
              <span className="text-sm font-normal">/{interval === 'year' ? 'an' : 'mois'}</span>
            </p>
          </div>

          <div className="p-6">
            {step === 'form' ? (
              <>
                {/* Social Login */}
                <div className="space-y-3 mb-6">
                  <button
                    onClick={() => handleOAuth('google')}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-zinc-900 font-medium rounded-xl hover:bg-zinc-100 transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continuer avec Google
                  </button>
                  
                  <button
                    onClick={() => handleOAuth('github')}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-colors border border-zinc-700"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                    Continuer avec GitHub
                  </button>
                </div>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-700"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-zinc-900 text-zinc-500">ou avec email</span>
                  </div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="vous@exemple.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="Minimum 8 caractères"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 text-red-300 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 px-6 bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Création du compte...
                      </>
                    ) : (
                      'Continuer vers le paiement'
                    )}
                  </button>
                </form>

                {/* Login link */}
                <p className="mt-6 text-center text-sm text-zinc-500">
                  Déjà un compte ?{' '}
                  <Link 
                    to={`/login?plan=${planSlug}&interval=${interval}&redirect=checkout`}
                    className="text-emerald-400 hover:underline"
                  >
                    Connectez-vous
                  </Link>
                </p>
              </>
            ) : (
              /* Payment Step */
              stripePromise && clientSecret && planDetails && (
                <Elements 
                  stripe={stripePromise} 
                  options={{
                    clientSecret,
                    appearance: {
                      theme: 'night',
                      variables: {
                        colorPrimary: '#10b981',
                        colorBackground: '#18181b',
                        colorText: '#fafafa',
                        colorDanger: '#ef4444',
                        borderRadius: '8px',
                        fontFamily: 'system-ui, sans-serif',
                      },
                      rules: {
                        '.Input': { backgroundColor: '#27272a', border: '1px solid #3f3f46' },
                        '.Input:focus': { border: '1px solid #10b981', boxShadow: '0 0 0 1px #10b981' },
                        '.Label': { color: '#a1a1aa' },
                      },
                    },
                  }}
                >
                  <PaymentForm 
                    clientSecret={clientSecret}
                    plan={planDetails}
                    accessToken={accessToken!}
                    onSuccess={handlePaymentSuccess}
                    onError={(err) => setError(err)}
                  />
                </Elements>
              )
            )}
          </div>
        </div>

        {/* Billing interval toggle */}
        <div className="mt-6 flex justify-center gap-4">
          <Link
            to={`/checkout?plan=${planSlug}&interval=month`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              interval === 'month' 
                ? 'bg-emerald-500 text-white' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            Mensuel
          </Link>
          <Link
            to={`/checkout?plan=${planSlug}&interval=year`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              interval === 'year' 
                ? 'bg-emerald-500 text-white' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            Annuel (-17%)
          </Link>
        </div>
      </div>
    </div>
  );
}
